import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, ScrollStrategy } from '@angular/cdk/overlay';
import { ViewportRuler } from '@angular/cdk/scrolling';
import {
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import moment from 'moment';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  _AbstractConstructor,
  _Constructor,
  CanDisable,
  CanUpdateErrorState,
  HasTabIndex,
  mixinDisabled,
  mixinErrorState,
  mixinTabIndex,
} from '@nimbus-library/utils/material-mixins-types';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import {
  MatTimeValuePart,
  MatTimeValueProcessor,
} from '@nimbus-library/features/shared/components/material/mat-time/mat-time-value.processor';
import { convertFromTeamsClientCoordinates, isContainsInSVG } from '@nimbus-library/features/shared/components/material/utils';
import { MatTimeService } from '@nimbus-library/features/shared/components/material/mat-time/mat-time.service';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { MatTimeAnimations } from '../mat-time.animations';

let nextUniqueId = 0;

class MatDurationPickerBase {
  public readonly stateChanges: Subject<void> = new Subject<void>();

  public constructor(
    public _elementRef: ElementRef,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl,
  ) {}
}

const MatDurationPickerMixinBase: _Constructor<HasTabIndex> &
  _AbstractConstructor<HasTabIndex> &
  _Constructor<CanDisable> &
  _AbstractConstructor<CanDisable> &
  _Constructor<CanUpdateErrorState> &
  _AbstractConstructor<CanUpdateErrorState> &
  typeof MatDurationPickerBase = mixinTabIndex(mixinDisabled(mixinErrorState(MatDurationPickerBase)));

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-mat-duration-picker',
  templateUrl: 'mat-duration-picker.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mat-time-picker',
    '[attr.id]': 'id',
    '[attr.tabindex]': 'tabIndex',
    '[class.mat-time-picker-disabled]': 'disabled',
    '[class.mat-time-picker-required]': 'required',
    '[class.mat-time-picker-empty]': 'empty',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '[attr.aria-invalid]': 'errorState',
    '[class.mat-time-picker-invalid]': 'errorState',
  },
  animations: [MatTimeAnimations.transformPanelWrap, MatTimeAnimations.transformPanel],
  providers: [{ provide: MatFormFieldControl, useExisting: MatDurationPickerComponent }],
})
export class MatDurationPickerComponent
  extends MatDurationPickerMixinBase
  implements OnDestroy, OnInit, DoCheck, ControlValueAccessor, CanDisable, HasTabIndex, MatFormFieldControl<any>, CanUpdateErrorState
{
  @Input()
  public disabled: boolean;
  @Input()
  public tabIndex: number;
  @Input()
  public set timeFormat(value: string) {
    if (value !== this.format) {
      this.format = value;
      this.isDaysEnabled = this.format && this.format.toLowerCase().includes('dd');
      this.isHoursEnabled = this.format && this.format.toLowerCase().includes('hh');
      this.isMinutesEnabled = this.format && this.format.toLowerCase().includes('mm');
      this.isSecondsEnabled = this.format && this.format.toLowerCase().includes('ss');
      this.stateChanges.next();
    }
  }

  public get timeFormat(): string {
    return this.format;
  }

  public get focused(): boolean {
    return this.isFocused || this.isPanelOpen;
  }

  @Input()
  public get placeholder(): string {
    return this.innerPlaceholder;
  }

  public set placeholder(value: string) {
    this.innerPlaceholder = value;
    this.stateChanges.next();
  }

  @Input()
  public get required(): boolean {
    return this.isRequired;
  }

  public set required(value: boolean) {
    this.isRequired = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  public get value(): number {
    return this.innerValue;
  }

  public set value(value: number) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.valueProcessor.parseValueSeconds(this.innerValue);
      this.propagateChange(value);
    }
  }

  @Input()
  public get id(): string {
    return this._id;
  }

  public set id(value: string) {
    this._id = value || this._uid;
    this.stateChanges.next();
  }

  public teamsClient$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((x) => x !== ClientType.Browser),
      )
    : of(false);

  public constructor(
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
    private _viewportRuler: ViewportRuler,
    private changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private elementReference: ElementRef<HTMLElement>,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional() private _parentFormField: MatFormField,
    @Self() @Optional() public ngControl: NgControl,
    @Attribute('tabindex') tabIndex: string,
    @Inject(MAT_SELECT_SCROLL_STRATEGY) scrollStrategyFactory: any,
    private matTimeService: MatTimeService,
  ) {
    super(elementReference, _defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.tabIndex = parseInt(tabIndex) || 0;
    this.valueProcessor = new MatTimeValueProcessor(this.matTimeService);
  }

  public get triggerValue(): string {
    if (this.empty) {
      return '';
    }
    const duration = moment.duration({ seconds: this.innerValue });
    return this.format
      .toLowerCase()
      .replace('dd', Math.floor(duration.asDays()).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }))
      .replace('hh', duration.hours().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }))
      .replace('mm', duration.minutes().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }))
      .replace('ss', duration.seconds().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }));
  }

  public get empty(): boolean {
    return !this.innerValue;
  }

  public get shouldLabelFloat(): boolean {
    return this.isPanelOpen || !this.empty;
  }

  public translations = TranslationKeys;

  public isPanelOpen: boolean = false;

  private _scrollTop = 0;

  private _uid = `mat-time-picker-${nextUniqueId++}`;

  private readonly _destroy = new Subject<void>();

  public _triggerRect: ClientRect;

  public _ariaDescribedby: string;

  public _panelDoneAnimatingStream = new Subject<string>();

  public _scrollStrategy: ScrollStrategy;

  public _offsetY = 0;

  public _positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  @ViewChild('trigger', { static: true })
  public trigger: ElementRef;

  @ViewChild('panel')
  public panel: ElementRef;

  @ViewChild(CdkConnectedOverlay, { static: true })
  public overlayDir: CdkConnectedOverlay;

  private innerPlaceholder: string;

  private isFocused: boolean = false;

  private isRequired: boolean = false;

  private innerValue: number;

  private format: string = 'HH:mm:ss';

  public isDaysEnabled: boolean = false;

  public isMinutesEnabled: boolean = true;

  public isHoursEnabled: boolean = true;

  public isSecondsEnabled: boolean = true;

  @ViewChild('daysInput')
  public daysInput: ElementRef<HTMLInputElement>;

  @ViewChild('hoursInput')
  public hoursInput: ElementRef<HTMLInputElement>;

  @ViewChild('minutesInput')
  public minutesInput: ElementRef<HTMLInputElement>;

  @ViewChild('secondsInput')
  public secondsInput: ElementRef<HTMLInputElement>;

  @Input()
  public errorStateMatcher: ErrorStateMatcher;

  private _id: string;

  public valueProcessor: MatTimeValueProcessor;

  public valuePart: any = MatTimeValuePart;

  public isScalable: boolean = false;

  public readonly autofilled: boolean;
  public readonly controlType: string;
  public defaultTabIndex: number;

  public readonly userAriaDescribedBy: string;

  public propagateChange: any = () => {};

  public propagateTouched: any = () => {};

  public ngOnInit(): void {
    this._panelDoneAnimatingStream.pipe(distinctUntilChanged(), takeUntil(this._destroy)).subscribe(() => {
      if (this.isPanelOpen) {
        this._scrollTop = 0;
      } else {
        this.overlayDir.offsetX = 0;
        this.changeDetectorRef.markForCheck();
      }
    });

    this._viewportRuler
      .change()
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        if (this.isPanelOpen) {
          this._triggerRect = this.trigger.nativeElement.getBoundingClientRect();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  public togglePanel(): void {
    this.isPanelOpen ? this.close() : this.open();
  }

  public open(): void {
    if (this.disabled || this.isPanelOpen) {
      return;
    }
    this._triggerRect = this.trigger.nativeElement.getBoundingClientRect();
    this.valueProcessor.parseValueSeconds(this.innerValue);

    this.isPanelOpen = true;
    this._calculateOverlayPosition();
    setTimeout(() => {
      if (this.daysInput) {
        this.daysInput.nativeElement.focus();
        return;
      }
      if (this.hoursInput) {
        this.hoursInput.nativeElement.focus();
      }
    }, 100);
    this.changeDetectorRef.markForCheck();
  }

  public onDaysValueChanged(value: string): void {
    if (value && value.length === 2 && this.isHoursEnabled && this.hoursInput) {
      this.hoursInput.nativeElement.focus();
    }
  }

  public onHoursValueChanged(value: string): void {
    if (value && value.length === 2 && this.isMinutesEnabled && this.minutesInput) {
      this.minutesInput.nativeElement.focus();
    }
  }

  public onMinutesValueChanged(value: string): void {
    if (value && value.length === 2 && this.isSecondsEnabled && this.secondsInput) {
      this.secondsInput.nativeElement.focus();
    }
  }

  public close(): void {
    if (this.isPanelOpen) {
      this.isPanelOpen = false;
      this.changeDetectorRef.markForCheck();
      this.propagateTouched();
    }
  }

  public writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.valueProcessor.parseValueSeconds(this.innerValue);
    }
    this.changeDetectorRef.markForCheck();
  }

  public onCancelClick(): void {
    this.close();
  }

  public onSaveClick(): void {
    this.value = this.valueProcessor.getDurationSeconds();
    this.close();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.propagateTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }

  public onFocus(): void {
    if (!this.disabled) {
      this.isFocused = true;
      this.stateChanges.next();
    }
  }

  public onBlur(): void {
    this.isFocused = false;

    if (!this.disabled && !this.isPanelOpen) {
      this.propagateTouched();
      this.changeDetectorRef.markForCheck();
      this.stateChanges.next();
    }
  }

  public onAttached(): void {
    this.overlayDir.positionChange.pipe(take(1)).subscribe(() => {
      this.changeDetectorRef.detectChanges();
      this._calculateOverlayOffsetX();
      this.panel.nativeElement.scrollTop = this._scrollTop;
    });
  }

  private _calculateOverlayPosition(): void {
    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global-scale') : 1;

    const panelHeight: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-time-picker-panel-max-height') || '115');

    const paddingTop = 29;
    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionY = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().y;
          const currentPositionY = convertFromTeamsClientCoordinates(currentScaledPositionY);
          this._offsetY =
            -1 * (currentScaledPositionY - currentPositionY - paddingTop * globalScale - (panelHeight * globalScale - panelHeight) / 2);
        } else {
          this._offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
        }
      } else {
        this._offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
      }
      this._scrollTop = 0;
      this._checkOverlayWithinViewport(0);
    });
  }

  private _checkOverlayWithinViewport(maxScroll: number): void {
    const panelHeight: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-time-picker-panel-max-height') || '115');
    const viewportSize = this._viewportRuler.getViewportSize();

    const topSpaceAvailable = this._triggerRect.top;
    const bottomSpaceAvailable = viewportSize.height - this._triggerRect.bottom;

    const panelHeightTop = Math.abs(this._offsetY);

    const panelHeightBottom = panelHeight - panelHeightTop - this._triggerRect.height;

    if (panelHeightBottom > bottomSpaceAvailable) {
      this.adjustPanelUp(panelHeightBottom, bottomSpaceAvailable);
    } else if (panelHeightTop > topSpaceAvailable) {
      this.adjustPanelDown(panelHeightTop, topSpaceAvailable, maxScroll);
    }
  }

  private adjustPanelUp(panelHeightBottom: number, bottomSpaceAvailable: number) {
    const distanceBelowViewport = Math.round(panelHeightBottom - bottomSpaceAvailable);

    this._scrollTop -= distanceBelowViewport;
    this._offsetY -= distanceBelowViewport;

    if (this._scrollTop <= 0) {
      this._scrollTop = 0;
      this._offsetY = 0;
    }
  }

  private adjustPanelDown(panelHeightTop: number, topSpaceAvailable: number, maxScroll: number) {
    const distanceAboveViewport = Math.round(panelHeightTop - topSpaceAvailable);
    this._scrollTop += distanceAboveViewport;
    this._offsetY += distanceAboveViewport;

    if (this._scrollTop >= maxScroll) {
      this._scrollTop = maxScroll;
      this._offsetY = 0;
    }
  }

  private _calculateOverlayOffsetX(): void {
    const overlayRect = this.overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const viewportSize = this._viewportRuler.getViewportSize();

    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global-scale') : 1;

    let xPadding: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-field-flex-padding') || '12');

    const fieldWidth: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-field-width') || '300');
    xPadding = xPadding * globalScale;

    const paddingWidth = xPadding * 2;
    let offsetX: number;

    offsetX = xPadding;

    offsetX *= -1;

    // Determine how much the select overflows on each side.

    let leftOverflow = 0 - (overlayRect.left + offsetX);
    let rightOverflow = overlayRect.right + offsetX - viewportSize.width + paddingWidth;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        leftOverflow = 0 - (convertFromTeamsClientCoordinates(overlayRect.left) + offsetX);
        rightOverflow = convertFromTeamsClientCoordinates(overlayRect.right) + offsetX - viewportSize.width + paddingWidth;
      }
      if (leftOverflow > 0) {
        offsetX += leftOverflow;
      } else if (rightOverflow > 0) {
        offsetX -= rightOverflow;
      }

      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionX = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().x;
          const currentPositionX = convertFromTeamsClientCoordinates(currentScaledPositionX);
          this.overlayDir.offsetX =
            -1 * (currentScaledPositionX - currentPositionX - offsetX - (fieldWidth * globalScale - fieldWidth) / 2);
        } else {
          this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
        }
      } else {
        this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
      }

      this.overlayDir.overlayRef.updatePosition();
    });
  }

  public setDescribedByIds(ids: string[]) {
    this._ariaDescribedby = ids.join(' ');
  }

  public onContainerClick() {
    this.focus();
    this.open();
  }

  public focus(options?: FocusOptions): void {
    this._elementRef.nativeElement.focus(options);
  }

  public ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this.stateChanges.complete();
  }

  public updateErrorState(): void {
    super.updateErrorState();
  }
}
